import React, { useState, Fragment, useRef, useImperativeHandle, forwardRef, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationIcon } from '@heroicons/react/outline';
import { InformationCircleIcon } from '@heroicons/react/outline';
import { BadgeCheckIcon } from '@heroicons/react/outline';
import DarkModeContext from '../Components/DarkModeContext';

const Modal = forwardRef((props, ref) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('info');
  const [titleModal, setTitleModal] = useState('');
  const [contentModal, setContentModal] = useState('');
  const [customButtons, setCustomButtons] = useState(null);
  const [width, setWidth] = useState('sm:max-w-lg'); // Default width
  const { darkMode } = useContext(DarkModeContext);

  const cancelButtonRef = useRef(null);

  const closeModal = () => {
    setOpen(false);
  };

  const openModal = (type, title, content, customButtons, modalWidth = 'sm:max-w-lg') => {
    setType(type);
    setTitleModal(title);
    setContentModal(content);
    setCustomButtons(customButtons);
    setWidth(modalWidth);
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({ closeModal, openModal }));

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-confirm" initialFocus={cancelButtonRef} onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed z-confirm inset-0 transition-opacity bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed z-confirm inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-dvh px-4 text-center sm:block sm:p-0">
            <span className="hidden sm:inline-block sm:align-middle sm:h-dvh" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={`ap-card relative z-50 inline-block align-middle text-left overflow-hidden transform transition-all sm:my-8 w-full ${width}`} style={{ borderRadius: 20, color: 'var(--ap-label)', boxShadow: '0 24px 64px rgba(0,0,0,.3)' }}>
                <div className="px-4 pt-6 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    {type === 'info' && (
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-500/15 sm:mx-0 sm:h-10 sm:w-10">
                        <InformationCircleIcon className={`h-6 w-6 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`} aria-hidden="true" />
                      </div>
                    )}
                    {type === 'success' && (
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-500/15 sm:mx-0 sm:h-10 sm:w-10">
                        <BadgeCheckIcon className={`h-6 w-6 ${darkMode ? 'text-green-300' : 'text-green-600'}`} aria-hidden="true" />
                      </div>
                    )}
                    {type === 'warning' && (
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-500/15 sm:mx-0 sm:h-10 sm:w-10">
                        <ExclamationIcon className={`h-6 w-6 ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`} aria-hidden="true" />
                      </div>
                    )}
                    {type === 'error' && (
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-500/15 sm:mx-0 sm:h-10 sm:w-10">
                        <ExclamationIcon className={`h-6 w-6 ${darkMode ? 'text-red-300' : 'text-red-600'}`} aria-hidden="true" />
                      </div>
                    )}
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title as="h3" className="ap-title-2">
                        {titleModal}
                      </Dialog.Title>
                      <div className="mt-2">
                        {contentModal}
                      </div>
                    </div>
                  </div>
                </div>
                {customButtons && customButtons}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
});

export default Modal;
